/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'

import { CreatePlanItemInput, CatalogSearchResultFragment } from '../../../../../generated/graphql'
import { SecondaryButton } from '../../../../shared/buttons'
import CourseAutoComplete from '../../../../courses/CourseAutoComplete'
import useAppSettings from '../../../../../lib/hooks/useAppSettings'
import useMixpanelEvent from '../../../../../lib/hooks/useMixpanelEvent'

const containerCss = css`
  padding-bottom: 25px;
`

const cancelButtonCss = css`
  float: right;
`

export type PlanItemCourseInputProps = {
  /** Campus used to filter courses */
  campus?: string
  /** Current Group Id */
  groupId: string
  /** Only show courses, no option to add placeholders */
  isCourseOnly?: boolean
  /** If the plan is a template */
  isTemplate: boolean
  /** Event callback when a new Catalog Course is selected or placeholder is added */
  onAdd: (planItem: CreatePlanItemInput) => void
  /** Event callback when the Cancel button is clicked */
  onCancelAdd: () => void
  /** Plan id */
  planId: string
  /** Term Code used for finding courses that are active for this term */
  termCode?: string
}

const defaultProps: Pick<PlanItemCourseInputProps, 'campus' | 'isCourseOnly' | 'termCode'> = {
  campus: '',
  isCourseOnly: false,
  termCode: ''
}

export const PlanItemCourseInput = ({
  campus,
  groupId,
  isCourseOnly,
  isTemplate,
  onAdd,
  onCancelAdd,
  planId,
  termCode
}: PlanItemCourseInputProps) => {
  const { text } = useAppSettings()
  const { course: courseLabel } = text
  const { addMixpanelEvent } = useMixpanelEvent()

  const handleChange = (course: CatalogSearchResultFragment | CatalogSearchResultFragment[]) => {
    const {
      courseNumber,
      description,
      externalCourseId,
      subjectId,
      title,
      creditsMin,
      creditsMax
    } = course as CatalogSearchResultFragment
    const isCourseItem = courseNumber && subjectId

    onAdd({
      courseNumber,
      creditsMax:
        isCourseItem && typeof creditsMax === 'number' && creditsMax >= 0 ? creditsMax : undefined,
      creditsMin:
        isCourseItem && typeof creditsMin === 'number' && creditsMin >= 0 ? creditsMin : undefined,
      description,
      externalCourseId,
      nonCourseCreditsMax: !isCourseItem ? creditsMax : undefined,
      nonCourseCreditsMin: !isCourseItem ? creditsMin : undefined,
      nonCourseTitle: !isCourseItem ? title : '',
      groupId,
      planId,
      subjectId,
      title: isCourseItem ? title : ''
    })

    if (!isTemplate) {
      addMixpanelEvent('Student Profile > Plan: Add Course to Plan')
    }
  }

  return (
    <div css={containerCss}>
      <CourseAutoComplete
        campus={campus}
        groupId={groupId}
        label={isCourseOnly ? courseLabel : 'Item'}
        onChange={handleChange}
        showStaticOption={!isCourseOnly}
        termCode={termCode}
      />
      <SecondaryButton
        ariaLabel="Cancel adding a new item"
        onClick={onCancelAdd}
        css={cancelButtonCss}
      >
        CANCEL
      </SecondaryButton>
    </div>
  )
}

PlanItemCourseInput.defaultProps = defaultProps

export default PlanItemCourseInput
